import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Lock, Sparkles, PowerOff } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useWorkspaceAccess, useWorkspaceLimits } from '@/lib/billing'
import type { Feature } from '@/lib/billing'
import { SHOW_BILLING_TAB } from '@/lib/featureFlags'

/**
 * Wraps a route element so it only renders for workspaces with access to
 * a specific feature (or any of a set of features). Otherwise renders an
 * upgrade splash that points the user to the billing page.
 *
 * Usage:
 *   <Route path="field" element={<GatedRoute feature="field"><FieldHub /></GatedRoute>} />
 *   <Route path="products" element={<GatedRoute features={['quotes', 'invoices']}><Products /></GatedRoute>} />
 *
 * Pass either `feature` (single) or `features` (any-of array). Exactly one
 * of these is required — the discriminated union below enforces it at the
 * type level.
 *
 * Loading and error states fail closed (render the splash) to be consistent
 * with how the rest of the gating hooks behave. A small loading state shows
 * briefly during the first hook fetch — usually <300ms — to avoid flashing
 * the splash before the access query has resolved.
 */
type GatedRouteProps =
  | { feature: Feature; features?: never; children: React.ReactNode }
  | { feature?: never; features: Feature[]; children: React.ReactNode }

export default function GatedRoute(props: GatedRouteProps) {
  const { workspace } = useAuth()
  const { data: access, isLoading: accessLoading } = useWorkspaceAccess()
  const { data: limits, isLoading: limitsLoading } = useWorkspaceLimits()

  // Loading: show a minimal skeleton while either query resolves.
  // Avoids a flash-of-splash on first paint.
  if (accessLoading || limitsLoading || !access || !limits) {
    return <GateLoadingState />
  }

  // No access at all (canceled, expired trial, etc.) — splash.
  if (!access.hasAccess) {
    return <UpgradeSplash reason="no_access" />
  }

  // Determine which features grant access for this gate.
  const required = 'features' in props && props.features
    ? props.features
    : [props.feature as Feature]

  const granted = required.some(f => limits.features.includes(f))

  if (!granted) {
    // AI is the one feature with both tier-gating AND a workspace-level
    // opt-out. When the gate fails on AI, distinguish the two so the splash
    // can surface the right action (upgrade plan vs flip a toggle in
    // Settings → AI). The opt-out filter happens inside get_workspace_limits
    // (see 20260502120000_workspaces_ai_enabled.sql), so by the time we get
    // here the limits.features array doesn't tell us which case it was.
    // We re-derive: tier grants AI iff tier ∈ {pro, enterprise}; combined
    // with workspace.ai_enabled, the four cases collapse to two splash
    // variants. Edge case (tier doesn't grant + opted out) → 'feature_locked'
    // because flipping the toggle won't help; upgrading the plan will.
    const isAIRequest = required.includes('ai')
    const tierGrantsAI = access.tier === 'pro' || access.tier === 'enterprise'
    const optedOut = workspace?.ai_enabled === false
    if (isAIRequest && tierGrantsAI && optedOut) {
      return <UpgradeSplash reason="opted_out" feature="ai" />
    }
    // Single-feature gates name the feature in the splash. Multi-feature
    // OR-gates (e.g. ['quotes', 'invoices', 'field']) can't name "the" feature
    // honestly — picking required[0] would mis-label the others — so we hand
    // the splash undefined and let it fall back to generic copy.
    return <UpgradeSplash reason="feature_locked" feature={required.length === 1 ? required[0] : undefined} />
  }

  return <>{props.children}</>
}

// ─── Internal: Loading state ────────────────────────────────────────────────
// Subtle, dark-theme-matching, non-flashy. Mounts and unmounts quickly so
// elaborate skeletons aren't worth the visual noise.

function GateLoadingState() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}  // Don't show at all for the common <150ms case.
        style={{
          width: 28,
          height: 28,
          border: '2px solid var(--border-default)',
          borderTopColor: 'var(--focus-ring)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
    </div>
  )
}

// ─── Internal: Upgrade splash ───────────────────────────────────────────────
// Full content-area replacement. Reuses the visual language of
// PortalComingSoon (dark bg, centered card, gradient icon) so it feels
// native to the rest of the app.

const FEATURE_DISPLAY_NAMES: Record<Feature, string> = {
  contacts:     'Contacts',
  inbox:        'Inbox',
  deals:        'Deals',
  quotes:       'Quotes',
  invoices:     'Invoices',
  field:        'Field',
  reputation:   'Reputation',
  projects:     'Projects',
  integrations: 'Integrations',
  analytics:    'Analytics',
  marketing:    'Marketing',
  ai:           'AI',
  fleet:        'Fleet',
}

function UpgradeSplash({
  reason,
  feature,
}: {
  reason: 'no_access' | 'feature_locked' | 'opted_out'
  feature?: Feature
}) {
  const displayName = feature ? FEATURE_DISPLAY_NAMES[feature] : 'this feature'

  // Per-reason copy and CTA. opted_out points at the AI Settings toggle
  // (the action that resolves the splash), not the billing page.
  const variant = reason === 'no_access'
    ? {
      Icon: Lock,
      headline: 'Your workspace is locked',
      body: "You don't have an active subscription. Reactivate or upgrade your plan to continue.",
      ctaTo: '/settings/billing',
      ctaLabel: 'Go to billing',
    }
    : reason === 'opted_out'
    ? {
      Icon: PowerOff,
      headline: `${displayName} is disabled for this workspace`,
      body: `An admin has turned off ${displayName} features. Re-enable them in Settings → AI.`,
      ctaTo: '/settings/ai',
      ctaLabel: 'Go to AI settings',
    }
    : feature
    ? {
      Icon: Sparkles,
      headline: `Upgrade to access ${displayName}`,
      body: `${displayName} is available on a higher plan. Upgrade your subscription to unlock it.`,
      ctaTo: '/settings/billing',
      ctaLabel: 'Go to billing',
    }
    : {
      Icon: Sparkles,
      headline: "This feature isn't available on your plan",
      body: 'Upgrade your subscription to unlock it.',
      ctaTo: '/settings/billing',
      ctaLabel: 'Go to billing',
    }

  return (
    <div style={{
      minHeight: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}
      >
        <div style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: 'var(--logo-bg-gradient)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 0 40px var(--accent-glow)',
        }}>
          <variant.Icon style={{ width: 28, height: 28, color: 'var(--accent-ink)' }} />
        </div>

        <h1 style={{
          fontSize: 26,
          fontWeight: 900,
          color: 'var(--ink-primary)',
          letterSpacing: '-0.03em',
          margin: '0 0 12px',
        }}>
          {variant.headline}
        </h1>

        <p style={{
          fontSize: 15,
          color: 'var(--ink-secondary)',
          margin: '0 0 28px',
          lineHeight: 1.6,
        }}>
          {variant.body}
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            to="/app"
            className="btn-ghost"
            style={{ fontSize: 13, textDecoration: 'none' }}
          >
            Back to dashboard
          </Link>
          {(SHOW_BILLING_TAB || variant.ctaTo !== '/settings/billing') && (
            <Link
              to={variant.ctaTo}
              className="btn-primary"
              style={{ fontSize: 13, textDecoration: 'none' }}
            >
              {variant.ctaLabel}
            </Link>
          )}
        </div>
      </motion.div>
    </div>
  )
}
